import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import TextInput from '../../components/FormElements/TextInput'
import {
  useForm,
  FormProvider,
  FieldValues,
  useFormContext,
  Controller,
} from 'react-hook-form'
import { formatNumber } from '../../lib/utils/formatNumber'
import { useCreateAudience } from '../../api/dealership/customers/audiences/useCreateAudience'
import { useQueryEstimator } from '../../api/dealership/customers/audiences/useQueryEstimator'
import { Button, Tooltip, Toast, RangeDisplay } from '@demandlocal/components'
import { toast } from 'react-hot-toast'
import CustomQueryBuilder from '../../components/AudienceBuilder/QueryBuilder'
import { ReactComponent as AudienceCustomTypeIcon } from '../../images/audience/audience_custom_type_icon.svg'
import { ReactComponent as IconPeople } from '../../images/icons/icon_people.svg'
import { ReactComponent as IconCar } from '../../images/icons/icon_car.svg'
import { ReactComponent as IconService } from '../../images/icons/icon_service.svg'
import { ReactComponent as IconAI } from '../../images/icons/icon_ai.svg'
import { useDealership } from '../../store/zustand/dealershipStore'
import { upperFirst } from 'lodash'
import Select, { components } from 'react-select'
import AudienceSuggestionAction from '../../components/Suggestions/AudienceSuggestionAction'
import { useNavigate, useParams } from 'react-router'
import { useAiQueryEstimator } from '../../api/dealership/customers/audiences/useAiQueryEstimator'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { match } from 'ts-pattern'
import useReportAudienceOpenAIQuery from '../../api/dealership/audiences/useReportAudienceOpenAIQuery'
import { useGetAudienceById } from '../../api/dealership/customers/audiences/useGetAudienceById'
import LoadingAnimation from '../../components/LoadingAnimation'
import { useTranslation } from 'react-i18next'
import { queryClient } from '../../api/queryClient'
import type { AudienceFilter } from '../../components/AudienceBuilder/types'
import { useAiQueryGenerator } from '../../api/dealership/customers/audiences/useAiQueryGenerator'
import NumberFlow from '@number-flow/react'

// Create default values factory
const createDefaultAudienceFilter = (): AudienceFilter => ({
  groups: [
    {
      type: 'group',
      name: 'Group 1',
      rules: [
        {
          type: 'filter',
        },
      ],
    },
  ],
})

const AudienceDetailsManual = ({
  filteredAudience,
  isEstimatingAudienceSize,
  onQueryChange,
  setAudienceType,
  audienceType,
  isAutomaticAudience,
}: {
  isEstimatingAudienceSize: boolean
  filteredAudience: {
    count: number
  }
  onQueryChange: () => void
  setAudienceType: Dispatch<SetStateAction<string>>
  setFilteredAudience?: Dispatch<
    SetStateAction<{
      count: number
    }>
  >
  audienceType: string
  isAutomaticAudience: boolean
}): JSX.Element => {
  const placeHolder = match(audienceType)
    .with('leads', () => 'People who submitted a lead this month')
    .with('sales', () => 'Past customers who purchased a car within a year')
    .with('service', () => 'People who serviced their car this month')
    .otherwise(() => 'People who submitted a lead this month')
  const { setValue, getValues, reset } = useFormContext()
  const [showSuggestions, setShowSuggestions] = useState<boolean>(true)
  const { mutateAsync: aiQueryGenerator } = useAiQueryGenerator()

  const { audience_id } = useParams()

  const onAudienceDescriptionSubmit = async (e): Promise<void> => {
    e.preventDefault()
    const description = getValues('ai_description')
    const toastId = toast.loading('Generating query')
    try {
      const response = await aiQueryGenerator({
        audience_description: description,
        audience_id: audience_id ?? '',
      })
      reset({
        groups: response.data,
        audience_type: audienceType,
      })
      toast.success('Query generated successfully', { id: toastId })
    } catch (err) {
      toast.error('Error generating query', {
        id: toastId,
      })
    }
  }

  return (
    <div>
      {isAutomaticAudience && (
        <>
          <div className={'pt-12'}>
            <h3 className={'font-semibold text-primary-xiketic text-xs'}>
              Try natural language to generate your audiences using our new AI (beta).
            </h3>
          </div>
          <div className={'pt-4 mb-2'}>
            <Controller
              render={({ field }) => {
                return (
                  <div
                    className={
                      'bg-[#0DBDE333] p-4 flex gap-2 justify-between rounded-md'
                    }>
                    <div className={'flex w-3/4 gap-2'}>
                      <IconAI />
                      <input
                        type={'text'}
                        {...field}
                        className={
                          'bg-transparent w-full text-primary-persian-blue placeholder-primary-persian-blue/40 font-bold focus:outline-none'
                        }
                        placeholder={`Ex. ${placeHolder}`}
                      />
                    </div>
                    <Button
                      animate={false}
                      variant={'rainbow'}
                      onClick={onAudienceDescriptionSubmit}>
                      Generate audience
                    </Button>
                  </div>
                )
              }}
              name={'ai_description'}
            />
          </div>
        </>
      )}
      <div className={'pt-12 flex flex-row justify-between items-center'}>
        <Tooltip label={'Users who have at least one email address or a phone number'}>
          <div className={'flex flex-col items-start cursor-pointer gap-2'}>
            <div>
              <span className={'font-medium text-base text-primary-xiketic'}>
                {isEstimatingAudienceSize ? 'Estimating...' : 'Audience size'}
              </span>
            </div>
            <div className="bg-secondary-sunglow/20 p-3 font-bold">
              <NumberFlow
                value={filteredAudience.count}
                className={'part-[suffix]:text-primary-persian-blue'}
              />
            </div>
          </div>
        </Tooltip>
        {filteredAudience.count < 1000 && (
          <Toast className={'mr-2'} variant="error" text={'Your audience is too small'} />
        )}
      </div>
      <div className={'mt-7'}>
        <CustomQueryBuilder onChange={onQueryChange} />
      </div>
      <div className="pt-4 flex flex-col gap-2">
        <div className="flex flex-row justify-between items-center p-2">
          <span className="font-bold text-primary-persian-blue">Suggested audiences</span>
          <span
            className="cursor-pointer text-primary-persian-blue text-xs"
            onClick={(e) => {
              e.preventDefault()
              setShowSuggestions(!showSuggestions)
            }}>
            {showSuggestions ? 'Hide' : 'Show'} suggestions
          </span>
        </div>
        {showSuggestions && (
          <div className="flex flex-col gap-2 ">
            <AudienceSuggestionAction
              reason={"Because you've connected customers"}
              title={'People who submitted a lead this month'}
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.preventDefault()
                setAudienceType('leads')
                onQueryChange()
              }}
            />
            <AudienceSuggestionAction
              reason={'Because youre connected sales customers'}
              title={'Past customers who purchased a car within a year'}
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.preventDefault()
                setAudienceType('sales')
                onQueryChange()
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

const AudienceDetailsAutomatic = ({
  setFilteredAudience,
  filteredAudience,
  audienceType,
  isAutomaticAudience,
}: any): JSX.Element => {
  const placeHolder = match(audienceType)
    .with('leads', () => 'People who submitted a lead this month')
    .with('sales', () => 'Past customers who purchased a car within a year')
    .with('service', () => 'People who serviced their car this month')
    .otherwise(() => 'People who submitted a lead this month')
  const { audience_id } = useParams()
  const { getValues, watch } = useFormContext()
  const { mutate: reportQueryLog } = useReportAudienceOpenAIQuery()
  const { mutate: postAIQueryEstimator, isLoading, isIdle } = useAiQueryEstimator()
  const [openAiEstimatorResponse, setOpenAiEstimatorResponse] = useState<
    { _id?: string; count: number; query: Record<string, unknown> } | undefined
  >(undefined)
  const aiDescriptionValue = watch('ai_description')
  const onAudienceDescriptionSubmit = useCallback(
    (e) => {
      e.preventDefault()
      const toastId = toast.loading('Running query...')
      const description = getValues('ai_description')
      if (description && audience_id) {
        postAIQueryEstimator(
          {
            audience_description: description,
            audience_id: audience_id,
          },
          {
            onSuccess: (data) => {
              setFilteredAudience(data?.data)
              setOpenAiEstimatorResponse(data?.data)
              toast.success('Query ran successfully', { id: toastId })
            },
            onError: () => {
              toast.error('Error running query', { id: toastId })
            },
          },
        )
      } else {
        toast.error('Error running query', { id: toastId })
      }
    },
    [audience_id, getValues, postAIQueryEstimator, setFilteredAudience],
  )
  return (
    <div>
      {isAutomaticAudience && (
        <>
          <div className={'pt-12'}>
            <h3 className={'font-semibold text-primary-xiketic text-xs'}>
              Try natural language to generate your audiences using our new AI (beta).
            </h3>
          </div>
          <div className={'pt-4 mb-2'}>
            <Controller
              render={({ field }) => {
                return (
                  <div
                    className={
                      'bg-[#0DBDE333] p-4 flex gap-2 justify-between rounded-md'
                    }>
                    <div className={'flex w-3/4 gap-2'}>
                      <IconAI />
                      <input
                        type={'text'}
                        {...field}
                        className={
                          'bg-transparent w-full text-primary-persian-blue placeholder-primary-persian-blue/40 font-bold focus:outline-none'
                        }
                        placeholder={`Ex. ${placeHolder}`}
                      />
                    </div>
                    <Button
                      disabled={
                        aiDescriptionValue ? aiDescriptionValue?.length === 0 : true
                      }
                      animate={false}
                      variant={'rainbow'}
                      onClick={onAudienceDescriptionSubmit}>
                      {isLoading ? 'Generating' : 'Generate audience'}
                    </Button>
                  </div>
                )
              }}
              name={'ai_description'}
            />
          </div>
        </>
      )}
      {!isIdle && !isLoading && (
        <div className={'flex flex-col items-start gap-2 mb-8'}>
          <span className={'text-xs text-web-gray'}>
            Ensure Data Accuracy: Should the query results differ from your dealership's
            records, please submit a discrepancy report for us to rectify the issue.
          </span>
          <button
            onClick={async (e) => {
              e.preventDefault()
              if (openAiEstimatorResponse && audience_id) {
                const toastId = toast.loading('Submitting report...')
                await reportQueryLog({
                  audience_id: audience_id,
                  query_log_id: openAiEstimatorResponse?._id ?? '',
                })
                toast.success('Report submitted successfully', { id: toastId })
              }
            }}
            className={'text-xs font-bold text-secondary-bittersweet-red'}>
            Submit Discrepancy Report
          </button>
        </div>
      )}
      <AnimatePresence>
        {!isIdle && !isLoading && (
          <motion.div
            layout={'size'}
            initial="collapsed"
            exit="collapsed"
            animate={'open'}
            variants={{
              open: { opacity: 1, height: 'auto' },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{ type: 'spring', mass: 1, damping: 50, stiffness: 500 }}
            className={'overflow-hidden'}>
            <div className={'bg-cultured-gray p-5 rounded-md'}>
              <h3 className={'text-xs font-bold text-primary-xiketic'}>
                Based on your prompt, you are targeting
              </h3>
              <div className={'grid grid-cols-3 pt-3'}>
                <div className={'bg-white p-5 rounded-md flex flex-col gap-1'}>
                  <h4 className={'font-bold text-primary-xiketic text-sm'}>
                    Audience size
                  </h4>
                  <h2>{formatNumber(filteredAudience)}</h2>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
type Props = any

const AudienceDetails = (props: Props): JSX.Element => {
  const { audience_id } = useParams()
  const { onAudienceCreated, audienceType: _audienceType } = props
  const [audienceType, setAudienceType] = useState<Props['audienceType'] | string>(
    _audienceType,
  )
  const formContext = useForm({
    mode: 'onSubmit',
    defaultValues: {
      ...createDefaultAudienceFilter(),
      audience_type: audienceType,
    },
  })
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { isSubmitting, isDirty },
  } = formContext
  const navigate = useNavigate()
  const [filteredAudience, setFilteredAudience] = useState({ count: 0 })
  const { mutate: estimateAudienceSize, isLoading: isEstimatingAudienceSize } =
    useQueryEstimator()

  const { data: audience, isLoading } = useGetAudienceById(audience_id)

  const [audienceCreationType, setAudienceCreationType] = useState<
    'automatic' | 'manual'
  >('manual')

  const isAutomaticAudience = useMemo(
    () => audienceCreationType === 'automatic',
    [audienceCreationType],
  )

  const dealership = useDealership()

  const { t } = useTranslation('routes')

  const { mutateAsync: createAudience } = useCreateAudience()

  const onQueryChange = useCallback(() => {
    const newRules = getValues('groups') ?? []
    estimateAudienceSize(
      {
        queryData: newRules,
      },
      {
        onSuccess: (data) => {
          setFilteredAudience(data?.data)
        },
      },
    )
  }, [getValues, estimateAudienceSize])

  useEffect(() => {
    if (audience?.creation_status === 'complete') {
      onAudienceCreated({
        formValues: audience,
        audience_count: audience?.audience_size,
        audience_id: audience._id,
      })
    }
  }, [audience, onAudienceCreated])

  const onSubmit = async (values: FieldValues): Promise<void> => {
    const toastId = toast.loading('Creating audience...')
    await createAudience({
      data: {
        audience_id,
        audience_name: values?.audience_name,
        audience_type: audienceType,
        description: values?.description,
        audience_creation_type: audienceCreationType,
        filters: values.groups,
      },
    })
      .then((response: { audience_id: string }) => {
        queryClient.refetchQueries(['dealership', dealership?.id, 'audiences'])
        queryClient.refetchQueries(['dealership', dealership?.id, 'stats'])
        toast.success('Audience created successfully', { id: toastId })
        onAudienceCreated({
          formValues: getValues(),
          audience_count: filteredAudience || 0,
          audience_id: response.audience_id,
        })
      })
      .catch((err) => {
        toast.error(err.message, { id: toastId, duration: 5000 })
      })
  }

  if (isLoading) {
    return (
      <div className={'flex flex-1 h-full flex-col items-center justify-center'}>
        <LoadingAnimation title={'Loading audience'} />
      </div>
    )
  }

  return (
    <FormProvider {...formContext}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col pt-12 font-Spartan h-full'}>
        <div className={'flex flex-1 flex-col overflow-auto mb-4'}>
          <div className={'flex pt-1 px-12  justify-between'}>
            <h1 className={'font-bold text-3xl text-primary-xiketic'}>
              Build an Audience
            </h1>
            <div className={'flex flex-row items-center gap-4'}>
              <div className={'font-semibold text-sm text-primary-xiketic'}>
                Audience type
              </div>
              <div>
                <Select
                  isSearchable={false}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      minWidth: 140,
                      fontFamily: 'Spartan',
                      fontWeight: 'bold',
                      fontSize: 14,
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      fontFamily: 'Spartan',
                      fontWeight: 'bold',
                      fontSize: 14,
                    }),
                    singleValue: (provided, state) => ({
                      ...provided,
                      fontFamily: 'Spartan',
                      fontWeight: 'bold',
                      fontSize: 14,
                    }),
                  }}
                  components={{
                    IndicatorSeparator: () => null,
                    Option: (props: any) => {
                      const Icon = props.data.icon
                      return (
                        <components.Option {...props}>
                          <div className={'flex gap-2'}>
                            <div
                              className={
                                'bg-primary-persian-blue/10  rounded-full items-center flex justify-center p-1'
                              }>
                              <Icon className={'w-4 h-4 '} />
                            </div>
                            {upperFirst(props.data.label)}
                          </div>
                        </components.Option>
                      )
                    },
                    SingleValue: ({ children, ...props }: any) => {
                      const value = props.getValue()[0]
                      const Icon = props.options.find(
                        (option: { value: string }) => option.value === value.value,
                      )?.icon
                      return (
                        <components.SingleValue {...props}>
                          <div className={'flex gap-2'}>
                            <div
                              className={
                                'bg-primary-persian-blue/10  rounded-full items-center flex justify-center p-1'
                              }>
                              <Icon className={'w-4 h-4 '} />
                            </div>
                            {upperFirst(props.data.label)}
                          </div>
                        </components.SingleValue>
                      )
                    },
                  }}
                  defaultValue={{
                    label: upperFirst(audienceType),
                    value: audienceType,
                  }}
                  value={{ label: upperFirst(audienceType), value: audienceType }}
                  options={
                    [
                      { label: 'Leads', value: 'leads', icon: IconPeople },
                      { label: 'Service', value: 'service', icon: IconService },
                      { label: 'Sales', value: 'sales', icon: IconCar },
                      { label: 'Custom', value: 'custom', icon: AudienceCustomTypeIcon },
                    ] as Array<{
                      label: string
                      value: string
                      icon: FunctionComponent<SVGElement>
                    }>
                  }
                  onChange={(newValue) => {
                    if (
                      confirm(
                        'Are you sure you want to change audience type? This will erase any progress you have made.',
                      )
                    )
                      if (newValue) setAudienceType(newValue?.value)
                  }}
                />
              </div>
            </div>
          </div>
          <div className={'px-12 pt-12'}>
            <div className={' flex flex-row justify-between items-center'}>
              <span className={'font-bold text-base text-primary-xiketic'}>
                Who are you targeting
              </span>
              <div className={'bg-cultured-gray p-2 rounded-xl gap-3 flex flex-row'}>
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    setAudienceCreationType('manual')
                  }}
                  className={classNames('w-40 rounded-md font-bold text-sm', {
                    'bg-white py-1.5 text-primary-persian-blue drop-shadow-xl':
                      !isAutomaticAudience,
                  })}>
                  Manual targeting
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    setAudienceCreationType('automatic')
                  }}
                  className={classNames('w-40 rounded-md font-bold text-sm', {
                    'bg-white py-1.5 text-primary-persian-blue drop-shadow-xl':
                      isAutomaticAudience,
                  })}>
                  AI (beta)
                </button>
              </div>
            </div>
            <AudienceDetailsManual
              isEstimatingAudienceSize={isEstimatingAudienceSize}
              isAutomaticAudience={isAutomaticAudience}
              filteredAudience={filteredAudience}
              setFilteredAudience={setFilteredAudience}
              onQueryChange={onQueryChange}
              setAudienceType={setAudienceType}
              audienceType={audienceType}
            />
            <div className={'mt-8'}>
              <div>
                <TextInput
                  placeholder={`Ex. ${upperFirst(audienceType)} customers`}
                  rules={{ required: 'This field is required' }}
                  inverted
                  name={'audience_name'}
                  label={'Audience name'}
                  control={control}
                />
              </div>
            </div>
            <div className={'pt-2'}>
              <div>
                <div>
                  <TextInput
                    rules={{ required: 'This field is required' }}
                    inverted
                    placeholder={`Ex. ${upperFirst(audienceType)} customers`}
                    name={'description'}
                    label={'Description'}
                    control={control}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={'h-20 items-center flex justify-between border-t-2 py-4 px-12'}>
          <Button
            variant={'tertiary'}
            onClick={(e) => {
              e.preventDefault()
              navigate(t('dealership.audiences.custom', { dealership }))
            }}>
            Close
          </Button>
          <Button disabled={filteredAudience.count === 0 || isSubmitting} type={'submit'}>
            Create audience
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
export default AudienceDetails
